import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { trades as tradesApi } from '../api/client';
import { captureAndUpload } from '../utils/chartCapture';

let nextClientId = 1;
function generateClientId() {
  return `pending_${Date.now()}_${nextClientId++}`;
}

/**
 * Compute P&L for a position.
 * Buy: (exitOrCurrent - entry) * volume
 * Sell: (entry - exitOrCurrent) * volume
 */
function computePnl(direction, entryPrice, exitOrCurrentPrice, volume) {
  const diff =
    direction === 'buy'
      ? exitOrCurrentPrice - entryPrice
      : entryPrice - exitOrCurrentPrice;
  // Use toPrecision to avoid floating-point rounding errors, then round to 2 decimals
  const raw = diff * volume;
  return parseFloat(raw.toPrecision(10));
}

/**
 * Check if a pending order should trigger on the given bar.
 */
function shouldTrigger(order, bar) {
  if (order.order_type === 'limit') {
    if (order.direction === 'buy') return bar.low <= order.price;
    if (order.direction === 'sell') return bar.high >= order.price;
  }
  if (order.order_type === 'stop') {
    if (order.direction === 'buy') return bar.high >= order.price;
    if (order.direction === 'sell') return bar.low <= order.price;
  }
  return false;
}

/**
 * Check if a stop-loss triggers on the given bar.
 */
function slTriggered(position, bar) {
  if (position.stop_loss == null) return false;
  if (position.direction === 'buy') return bar.low <= position.stop_loss;
  return bar.high >= position.stop_loss;
}

/**
 * Check if a take-profit triggers on the given bar.
 */
function tpTriggered(position, bar) {
  if (position.take_profit == null) return false;
  if (position.direction === 'buy') return bar.high >= position.take_profit;
  return bar.low <= position.take_profit;
}

/**
 * Hook to manage simulated trading during replay.
 *
 * Runs client-side: processes each bar for pending order triggers, SL/TP,
 * and P&L updates. Persists trades to the server on open/close.
 */
export default function useTradingEngine(sessionId, session, currentIndex, allCandles, chartContainerRef) {
  const [openPositions, setOpenPositions] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [closedTrades, setClosedTrades] = useState([]);
  const [realizedPnl, setRealizedPnl] = useState(0);
  const [initialized, setInitialized] = useState(false);

  // Refs for mutable state during bar processing (skip-forward safe)
  const openPosRef = useRef([]);
  const pendingRef = useRef([]);
  const closedRef = useRef([]);
  const realizedPnlRef = useRef(0);
  const lastProcessedIndexRef = useRef(0);

  // Queue of server calls to fire after processing
  const serverCallsRef = useRef([]);

  const startingCapital = session ? parseFloat(session.starting_capital) : 0;
  const spread = session ? parseFloat(session.spread_points) : 0;

  // ─── Initialization: load existing trades on session resume ───
  useEffect(() => {
    if (!sessionId || !session || initialized) return;

    tradesApi.list(sessionId).then((res) => {
      const existing = res.data;
      const open = [];
      const closed = [];
      let sumPnl = 0;

      for (const t of existing) {
        const trade = {
          id: t.id,
          order_type: t.order_type,
          direction: t.direction,
          volume: parseFloat(t.volume),
          entry_price: parseFloat(t.entry_price),
          entry_timestamp: t.entry_timestamp,
          stop_loss: t.stop_loss != null ? parseFloat(t.stop_loss) : null,
          take_profit: t.take_profit != null ? parseFloat(t.take_profit) : null,
        };

        if (t.status === 'open') {
          open.push(trade);
        } else {
          trade.exit_price = parseFloat(t.exit_price);
          trade.exit_timestamp = t.exit_timestamp;
          trade.pnl = parseFloat(t.pnl);
          trade.exit_reason = t.exit_reason;
          closed.push(trade);
          sumPnl += trade.pnl;
        }
      }

      openPosRef.current = open;
      closedRef.current = closed;
      realizedPnlRef.current = sumPnl;
      pendingRef.current = [];

      setOpenPositions(open);
      setClosedTrades(closed);
      setRealizedPnl(sumPnl);
      setPendingOrders([]);
      setInitialized(true);

      // Set lastProcessedIndex to currentIndex so we don't reprocess past bars
      lastProcessedIndexRef.current = currentIndex;
    }).catch((err) => {
      console.error('Failed to load trades:', err);
      setInitialized(true);
      lastProcessedIndexRef.current = currentIndex;
    });
  }, [sessionId, session, initialized, currentIndex]);

  // ─── Sync React state to refs when actions modify state directly ───
  const syncStateFromRefs = useCallback(() => {
    setOpenPositions([...openPosRef.current]);
    setPendingOrders([...pendingRef.current]);
    setClosedTrades([...closedRef.current]);
    setRealizedPnl(realizedPnlRef.current);
  }, []);

  // ─── Fire queued server calls ───
  const flushServerCalls = useCallback(() => {
    const calls = serverCallsRef.current;
    serverCallsRef.current = [];
    for (const call of calls) {
      call().catch((err) => console.error('Server sync error:', err));
    }
  }, []);

  // ─── Process a single bar (mutates refs, queues server calls) ───
  const processBar = useCallback(
    (barIndex) => {
      const bar = allCandles[barIndex - 1];
      if (!bar) return;

      const barTimestamp = new Date(bar.time * 1000).toISOString();

      // 1. Check pending orders for trigger
      const stillPending = [];
      for (const order of pendingRef.current) {
        if (shouldTrigger(order, bar)) {
          // Fill: create open position via server
          const fillPrice = order.price;
          const newPos = {
            id: null, // will be set by server response
            clientId: order.clientId,
            order_type: order.order_type,
            direction: order.direction,
            volume: order.volume,
            entry_price: fillPrice,
            entry_timestamp: barTimestamp,
            stop_loss: order.stop_loss,
            take_profit: order.take_profit,
          };
          newPos._mae = 0;
          newPos._mfe = 0;
          newPos._pnlCurve = [];
          openPosRef.current.push(newPos);

          // Queue server create
          const tradeData = {
            order_type: order.order_type,
            direction: order.direction,
            volume: order.volume,
            entry_price: fillPrice,
            entry_timestamp: barTimestamp,
            stop_loss: order.stop_loss,
            take_profit: order.take_profit,
            playbook: order.playbook || null,
          };
          const clientId = order.clientId;
          serverCallsRef.current.push(() =>
            tradesApi.create(sessionId, tradeData).then((res) => {
              // Update the position with server ID
              const pos = openPosRef.current.find((p) => p.clientId === clientId);
              if (pos) pos.id = res.data.id;
              // Auto-capture chart at entry
              captureAndUpload(chartContainerRef, sessionId, res.data.id, 'Entry');
            })
          );
        } else {
          stillPending.push(order);
        }
      }
      pendingRef.current = stillPending;

      // 2. Update MAE/MFE/pnlCurve + Check SL/TP on open positions
      const stillOpen = [];
      for (const pos of openPosRef.current) {
        // Track MAE/MFE/pnlCurve
        const unrealized = computePnl(pos.direction, pos.entry_price, bar.close, pos.volume);
        if (pos._mae === undefined) { pos._mae = 0; pos._mfe = 0; pos._pnlCurve = []; }
        if (unrealized < pos._mae) pos._mae = unrealized;
        if (unrealized > pos._mfe) pos._mfe = unrealized;
        pos._pnlCurve.push({ time: barTimestamp, pnl: Math.round(unrealized * 100) / 100 });

        // Trailing stop: move SL to lock in profits
        if (pos.trailing_distance && pos.stop_loss != null) {
          if (pos.direction === 'buy') {
            const newSl = bar.high - pos.trailing_distance;
            if (newSl > pos.stop_loss) pos.stop_loss = newSl;
          } else {
            const newSl = bar.low + pos.trailing_distance;
            if (newSl < pos.stop_loss) pos.stop_loss = newSl;
          }
        }

        if (slTriggered(pos, bar)) {
          const exitPrice = pos.stop_loss;
          const pnl = computePnl(pos.direction, pos.entry_price, exitPrice, pos.volume);
          const closedPos = {
            ...pos,
            exit_price: exitPrice,
            exit_timestamp: barTimestamp,
            pnl,
            exit_reason: 'stop_loss',
          };
          closedRef.current.push(closedPos);
          realizedPnlRef.current += pnl;

          if (pos.id) {
            const tradeId = pos.id;
            serverCallsRef.current.push(() =>
              tradesApi.close(sessionId, tradeId, {
                exit_price: exitPrice,
                exit_timestamp: barTimestamp,
                pnl,
                exit_reason: 'stop_loss',
                mae: pos._mae || 0,
                mfe: pos._mfe || 0,
                pnl_curve: pos._pnlCurve || [],
              }).then(() => captureAndUpload(chartContainerRef, sessionId, tradeId, 'Exit - Stop Loss'))
            );
          }
        } else if (tpTriggered(pos, bar)) {
          const exitPrice = pos.take_profit;
          const pnl = computePnl(pos.direction, pos.entry_price, exitPrice, pos.volume);
          const closedPos = {
            ...pos,
            exit_price: exitPrice,
            exit_timestamp: barTimestamp,
            pnl,
            exit_reason: 'take_profit',
          };
          closedRef.current.push(closedPos);
          realizedPnlRef.current += pnl;

          if (pos.id) {
            const tradeId = pos.id;
            serverCallsRef.current.push(() =>
              tradesApi.close(sessionId, tradeId, {
                exit_price: exitPrice,
                exit_timestamp: barTimestamp,
                pnl,
                exit_reason: 'take_profit',
                mae: pos._mae || 0,
                mfe: pos._mfe || 0,
                pnl_curve: pos._pnlCurve || [],
              }).then(() => captureAndUpload(chartContainerRef, sessionId, tradeId, 'Exit - Take Profit'))
            );
          }
        } else {
          stillOpen.push(pos);
        }
      }
      openPosRef.current = stillOpen;
    },
    [allCandles, sessionId, chartContainerRef]
  );

  // ─── Process bars on currentIndex change ───
  useEffect(() => {
    if (!initialized || !session || allCandles.length === 0) return;

    const lastProcessed = lastProcessedIndexRef.current;
    if (currentIndex <= lastProcessed) return;

    // Process each bar (including skipped ones)
    for (let i = lastProcessed + 1; i <= currentIndex; i++) {
      processBar(i);
    }
    lastProcessedIndexRef.current = currentIndex;

    // Sync React state and fire server calls
    syncStateFromRefs();
    flushServerCalls();
  }, [currentIndex, initialized, session, allCandles, processBar, syncStateFromRefs, flushServerCalls]);

  // ─── Computed values ───
  const currentBar = allCandles[currentIndex - 1] || null;

  const totalUnrealizedPnl = useMemo(() => {
    if (!currentBar || openPositions.length === 0) return 0;
    let total = 0;
    for (const pos of openPositions) {
      total += computePnl(pos.direction, pos.entry_price, currentBar.close, pos.volume);
    }
    return Math.round(total * 100) / 100;
  }, [openPositions, currentBar]);

  const currentCapital = startingCapital + realizedPnl;
  const equity = Math.round((currentCapital + totalUnrealizedPnl) * 100) / 100;

  // ─── Actions ───
  const placeMarketOrder = useCallback(
    (direction, volume, stopLoss, takeProfit, playbookId, trailingDistance) => {
      const bar = allCandles[currentIndex - 1];
      if (!bar) return;

      const entryPrice =
        direction === 'buy' ? bar.close + spread / 2 : bar.close - spread / 2;
      const barTimestamp = new Date(bar.time * 1000).toISOString();

      const tradeData = {
        order_type: 'market',
        direction,
        volume,
        entry_price: entryPrice,
        entry_timestamp: barTimestamp,
        stop_loss: stopLoss || null,
        take_profit: takeProfit || null,
        playbook: playbookId || null,
      };

      // Optimistic: add to local state immediately
      const newPos = {
        id: null,
        clientId: generateClientId(),
        ...tradeData,
        trailing_distance: trailingDistance || null,
      };
      openPosRef.current = [...openPosRef.current, newPos];
      setOpenPositions([...openPosRef.current]);

      // Persist to server
      tradesApi.create(sessionId, tradeData).then((res) => {
        newPos.id = res.data.id;
        setOpenPositions([...openPosRef.current]);
        // Auto-capture chart at entry
        captureAndUpload(chartContainerRef, sessionId, res.data.id, 'Entry');
      }).catch((err) => {
        console.error('Failed to create trade:', err);
        // Rollback
        openPosRef.current = openPosRef.current.filter((p) => p.clientId !== newPos.clientId);
        setOpenPositions([...openPosRef.current]);
      });
    },
    [allCandles, currentIndex, spread, sessionId, chartContainerRef]
  );

  const placePendingOrder = useCallback(
    (orderType, direction, price, volume, stopLoss, takeProfit, playbookId) => {
      const newOrder = {
        clientId: generateClientId(),
        order_type: orderType,
        direction,
        price,
        volume,
        stop_loss: stopLoss || null,
        take_profit: takeProfit || null,
        playbook: playbookId || null,
        createdAtIndex: currentIndex,
      };
      pendingRef.current = [...pendingRef.current, newOrder];
      setPendingOrders([...pendingRef.current]);
    },
    [currentIndex]
  );

  const cancelPendingOrder = useCallback((clientId) => {
    pendingRef.current = pendingRef.current.filter((o) => o.clientId !== clientId);
    setPendingOrders([...pendingRef.current]);
  }, []);

  const closePosition = useCallback(
    (positionId, exitReason = 'manual', closeVolume = null) => {
      const bar = allCandles[currentIndex - 1];
      if (!bar) return;

      const posIndex = openPosRef.current.findIndex((p) => p.id === positionId);
      if (posIndex === -1) return;

      const position = openPosRef.current[posIndex];
      const exitPrice = bar.close;
      const barTimestamp = new Date(bar.time * 1000).toISOString();

      if (closeVolume && closeVolume < position.volume) {
        // Partial close
        const pnl = computePnl(position.direction, position.entry_price, exitPrice, closeVolume);
        const closedPos = {
          ...position,
          volume: closeVolume,
          exit_price: exitPrice,
          exit_timestamp: barTimestamp,
          pnl,
          exit_reason: 'partial',
        };
        closedRef.current = [...closedRef.current, closedPos];
        realizedPnlRef.current += pnl;

        // Reduce volume on original position
        position.volume = Math.round((position.volume - closeVolume) * 10000) / 10000;
        openPosRef.current = [...openPosRef.current];

        syncStateFromRefs();

        // Server: create a closed trade for the partial, modify original volume
        tradesApi.create(sessionId, {
          order_type: position.order_type,
          direction: position.direction,
          volume: closeVolume,
          entry_price: position.entry_price,
          entry_timestamp: position.entry_timestamp,
          stop_loss: position.stop_loss,
          take_profit: position.take_profit,
        }).then((res) =>
          tradesApi.close(sessionId, res.data.id, {
            exit_price: exitPrice,
            exit_timestamp: barTimestamp,
            pnl,
            exit_reason: 'partial',
          })
        ).catch((err) => console.error('Failed to persist partial close:', err));

        if (positionId) {
          tradesApi.modify(sessionId, positionId, { volume: position.volume })
            .catch((err) => console.error('Failed to modify volume:', err));
        }
      } else {
        // Full close
        const pnl = computePnl(position.direction, position.entry_price, exitPrice, position.volume);
        const closedPos = {
          ...position,
          exit_price: exitPrice,
          exit_timestamp: barTimestamp,
          pnl,
          exit_reason: exitReason,
        };
        closedRef.current = [...closedRef.current, closedPos];
        realizedPnlRef.current += pnl;
        openPosRef.current = openPosRef.current.filter((p) => p.id !== positionId);

        syncStateFromRefs();

        if (positionId) {
          tradesApi.close(sessionId, positionId, {
            exit_price: exitPrice,
            exit_timestamp: barTimestamp,
            pnl,
            exit_reason: exitReason,
            mae: position._mae || 0,
            mfe: position._mfe || 0,
            pnl_curve: position._pnlCurve || [],
          }).then(() => captureAndUpload(chartContainerRef, sessionId, positionId, 'Exit'))
            .catch((err) => console.error('Failed to close trade:', err));
        }
      }
    },
    [allCandles, currentIndex, sessionId, syncStateFromRefs, chartContainerRef]
  );

  const modifyPosition = useCallback(
    (positionId, changes) => {
      const pos = openPosRef.current.find((p) => p.id === positionId);
      if (!pos) return;

      if (changes.stopLoss !== undefined) pos.stop_loss = changes.stopLoss;
      if (changes.takeProfit !== undefined) pos.take_profit = changes.takeProfit;
      if (changes.trailingDistance !== undefined) pos.trailing_distance = changes.trailingDistance || null;
      openPosRef.current = [...openPosRef.current];
      setOpenPositions([...openPosRef.current]);

      const data = {};
      if (changes.stopLoss !== undefined) data.stop_loss = changes.stopLoss;
      if (changes.takeProfit !== undefined) data.take_profit = changes.takeProfit;

      tradesApi.modify(sessionId, positionId, data)
        .catch((err) => console.error('Failed to modify trade:', err));
    },
    [sessionId]
  );

  // ─── Close all open positions (for session end) ───
  const closeAllPositions = useCallback(async () => {
    const bar = allCandles[currentIndex - 1];
    if (!bar) return;
    const barTimestamp = new Date(bar.time * 1000).toISOString();

    const promises = [];
    for (const pos of openPosRef.current) {
      const exitPrice = bar.close;
      const pnl = computePnl(pos.direction, pos.entry_price, exitPrice, pos.volume);
      const closedPos = {
        ...pos,
        exit_price: exitPrice,
        exit_timestamp: barTimestamp,
        pnl,
        exit_reason: 'manual',
      };
      closedRef.current.push(closedPos);
      realizedPnlRef.current += pnl;

      if (pos.id) {
        promises.push(
          tradesApi.close(sessionId, pos.id, {
            exit_price: exitPrice,
            exit_timestamp: barTimestamp,
            pnl,
            exit_reason: 'manual',
          })
        );
      }
    }
    openPosRef.current = [];
    syncStateFromRefs();
    await Promise.all(promises);
  }, [allCandles, currentIndex, sessionId, syncStateFromRefs]);

  // ─── Session stats computation ───
  const computeSessionStats = useCallback(() => {
    const allTrades = closedRef.current;
    const totalTrades = allTrades.length;
    if (totalTrades === 0) return { total_trades: 0 };

    const winners = allTrades.filter((t) => t.pnl > 0);
    const losers = allTrades.filter((t) => t.pnl < 0);

    const totalPnl = allTrades.reduce((sum, t) => sum + t.pnl, 0);
    const grossProfit = winners.reduce((sum, t) => sum + t.pnl, 0);
    const grossLoss = Math.abs(losers.reduce((sum, t) => sum + t.pnl, 0));

    const avgWin = winners.length > 0 ? grossProfit / winners.length : 0;
    const avgLoss = losers.length > 0 ? grossLoss / losers.length : 0;

    const largestWin = winners.length > 0 ? Math.max(...winners.map((t) => t.pnl)) : 0;
    const largestLoss = losers.length > 0 ? Math.min(...losers.map((t) => t.pnl)) : 0;

    const profitFactor = grossLoss > 0 ? grossProfit / grossLoss : grossProfit > 0 ? Infinity : 0;
    const winRate = (winners.length / totalTrades) * 100;

    // Expectancy = (winRate/100 * avgWin) - (lossRate/100 * avgLoss)
    const lossRate = (losers.length / totalTrades) * 100;
    const expectancy = (winRate / 100) * avgWin - (lossRate / 100) * avgLoss;

    // Max drawdown from realized P&L curve
    let peak = 0;
    let maxDrawdown = 0;
    let runningPnl = 0;
    for (const t of allTrades) {
      runningPnl += t.pnl;
      if (runningPnl > peak) peak = runningPnl;
      const dd = peak - runningPnl;
      if (dd > maxDrawdown) maxDrawdown = dd;
    }

    // Risk/reward ratio
    const riskRewardRatio = avgLoss > 0 ? avgWin / avgLoss : 0;

    const round2 = (n) => Math.round(n * 100) / 100;

    return {
      total_trades: totalTrades,
      winning_trades: winners.length,
      losing_trades: losers.length,
      breakeven_trades: totalTrades - winners.length - losers.length,
      win_rate: round2(winRate),
      total_pnl: round2(totalPnl),
      gross_profit: round2(grossProfit),
      gross_loss: round2(grossLoss),
      profit_factor: profitFactor === Infinity ? null : round2(profitFactor),
      avg_win: round2(avgWin),
      avg_loss: round2(avgLoss),
      largest_win: round2(largestWin),
      largest_loss: round2(largestLoss),
      expectancy: round2(expectancy),
      risk_reward_ratio: round2(riskRewardRatio),
      max_drawdown: round2(maxDrawdown),
    };
  }, []);

  return {
    // State
    openPositions,
    pendingOrders,
    closedTrades,
    currentCapital: Math.round(currentCapital * 100) / 100,
    realizedPnl: Math.round(realizedPnl * 100) / 100,
    totalUnrealizedPnl,
    equity,

    // Actions
    placeMarketOrder,
    placePendingOrder,
    cancelPendingOrder,
    closePosition,
    modifyPosition,
    closeAllPositions,
    computeSessionStats,
  };
}
